import React, { useState, useEffect, useCallback } from 'react';
import { fetchIncomeStatement, exportReport } from '../api';

export default function IncomeStatementReport({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(0, 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchIncomeStatement(authAxios, {
        start_date: startDate, end_date: endDate,
      });
      setData(result);
    } catch (e) {
      console.error('Failed to load income statement', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const blob = await exportReport(authAxios, {
        report: 'income-statement', format: fmt,
        start_date: startDate, end_date: endDate,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `income_statement_${startDate}_${endDate}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const fmt = (v) => v != null ? Number(v).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const Section = ({ title, rows, total, totalLabel }) => (
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="px-4 py-1.5 font-mono w-24">{r.code}</td>
              <td className="px-4 py-1.5">{r.name}</td>
              <td className="px-4 py-1.5 text-right w-36">{fmt(r.balance)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold bg-gray-50">
            <td colSpan={2} className="px-4 py-2">{totalLabel || `Total ${title}`}</td>
            <td className="px-4 py-2 text-right">{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Income Statement (Profit &amp; Loss)</h2>
        <div className="flex gap-2">
          <button onClick={() => handleExport('xlsx')} disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
            Excel
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting}
            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">
            PDF
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-sm text-gray-600 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {data && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            For the period {data.start_date} to {data.end_date}
          </p>

          <Section title="Revenue" rows={data.revenue} total={data.total_revenue} />

          {data.cost_of_sales && data.cost_of_sales.length > 0 && (
            <>
              <Section title="Cost of Sales" rows={data.cost_of_sales} total={data.total_cost_of_sales} />
              <div className="border-t-2 flex justify-between px-4 py-2 font-bold text-gray-700 bg-blue-50 rounded">
                <span>Gross Profit</span>
                <span>{fmt(data.gross_profit)}</span>
              </div>
            </>
          )}

          <Section title="Expenses" rows={data.expenses} total={data.total_expenses} />

          <div className="border-t-4 flex justify-between px-4 py-3 font-bold text-lg bg-gray-100 rounded">
            <span>Net {data.net_income >= 0 ? 'Profit' : 'Loss'}</span>
            <span className={data.net_income >= 0 ? 'text-green-700' : 'text-red-700'}>
              {fmt(Math.abs(data.net_income))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
